import { useEffect, useState } from "react";
import { useTheme } from "@/app/providers/ThemeContext";
import { FONT_MONO, FONT_SANS } from "@/shared/lib/typography";

/* ─── CSS keyframes (injected once) ──────────────────────────────────────── */
const keyframes = `
@keyframes blink {
  0%, 100% { opacity: 1; }
  50% { opacity: 0; }
}
@keyframes fadeInUp {
  from { opacity: 0; transform: translateY(18px); }
  to   { opacity: 1; transform: translateY(0); }
}
@keyframes glowPulse {
  0%   { opacity: 0; }
  15%  { opacity: 1; }
  40%  { opacity: 1; }
  60%  { opacity: 0; }
  100% { opacity: 0; }
}
@keyframes arrowFlow {
  0%   { opacity: 0.15; }
  20%  { opacity: 0.7; }
  50%  { opacity: 0.7; }
  80%  { opacity: 0.15; }
  100% { opacity: 0.15; }
}
`;

/* ─── Shared style tokens ────────────────────────────────────────────────── */
const mono = FONT_MONO;
const sans = FONT_SANS;

/* Total animation cycle: 6s. Each panel glows for ~2s, staggered. */
const CYCLE = "6s";

function getThemeColors(isDark: boolean) {
  return {
    bg: isDark ? "#0c0f14" : "#ffffff",
    background: isDark ? "#111111" : "#f7f7f5",
    bgCenter: isDark ? "#111111" : "#f7f7f5",
    border: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)",
    text: isDark ? "#ffffff" : "#111110",
    textMuted: isDark ? "rgba(255,255,255,0.5)" : "rgba(0,0,0,0.5)",
    textDim: isDark ? "rgba(255,255,255,0.35)" : "rgba(0,0,0,0.35)",
    cyan: "#00B8DB",
    green: "#34d399",
    greenBg: isDark ? "rgba(52,211,153,0.05)" : "rgba(52,211,153,0.1)",
    arrow: isDark ? "rgba(255,255,255,0.15)" : "rgba(0,0,0,0.15)",
    claude: "#D4A574",
  };
}

/* ─── Claude sunburst logo SVG ────────────────────────────────────────────── */
function ClaudeLogo({ size = 18 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      {/* 12 radiating spokes from center */}
      {Array.from({ length: 12 }).map((_, i) => {
        const angle = i * 30;
        const rad = (angle * Math.PI) / 180;
        const innerR = 4;
        const outerR = 10;
        const x1 = 12 + Math.cos(rad) * innerR;
        const y1 = 12 + Math.sin(rad) * innerR;
        const x2 = 12 + Math.cos(rad) * outerR;
        const y2 = 12 + Math.sin(rad) * outerR;
        return (
          <line
            key={i}
            x1={x1}
            y1={y1}
            x2={x2}
            y2={y2}
            stroke="#FDF0E2"
            strokeWidth="2.2"
            strokeLinecap="round"
          />
        );
      })}
    </svg>
  );
}

/* ─── Arrow SVG between panels with flow animation ────────────────────────── */
function Arrow({ vertical = false, delay = "0s" }: { vertical?: boolean; delay?: string }) {
  const { theme } = useTheme();
  const isDark = theme === "dark";
  const themeColors = getThemeColors(isDark);

  const animStyle: React.CSSProperties = {
    animation: `arrowFlow ${CYCLE} ease-in-out ${delay} infinite`,
  };

  if (vertical) {
    return (
      <svg
        width="24"
        height="32"
        viewBox="0 0 24 32"
        fill="none"
        style={{ display: "block", margin: "0 auto", flexShrink: 0, ...animStyle }}
      >
        <line x1="12" y1="2" x2="12" y2="26" stroke={themeColors.cyan} strokeWidth="1.5" />
        <polyline points="7,21 12,27 17,21" stroke={themeColors.cyan} strokeWidth="1.5" fill="none" />
      </svg>
    );
  }
  return (
    <svg
      width="32"
      height="24"
      viewBox="0 0 32 24"
      fill="none"
      style={{ display: "block", flexShrink: 0, ...animStyle }}
    >
      <line x1="2" y1="12" x2="26" y2="12" stroke={themeColors.cyan} strokeWidth="1.5" />
      <polyline points="21,7 27,12 21,17" stroke={themeColors.cyan} strokeWidth="1.5" fill="none" />
    </svg>
  );
}

/* ─── Small pill tag ─────────────────────────────────────────────────────── */
function Pill({ children }: { children: string }) {
  const { theme } = useTheme();
  const isDark = theme === "dark";
  const themeColors = getThemeColors(isDark);

  return (
    <span
      style={{
        fontFamily: mono,
        fontSize: "0.55rem",
        letterSpacing: "0.06em",
        color: themeColors.textMuted,
        border: `1px solid ${themeColors.border}`,
        borderRadius: "100px",
        padding: "3px 9px",
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </span>
  );
}

/* ─── Data row helper (label — value) ────────────────────────────────────── */
function DataRow({
  label,
  value,
  valueColor,
}: {
  label: string;
  value: string;
  valueColor?: string;
}) {
  const { theme } = useTheme();
  const isDark = theme === "dark";
  const themeColors = getThemeColors(isDark);

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "3px 0",
      }}
    >
      <span style={{ fontFamily: mono, fontSize: "0.65rem", color: themeColors.textMuted }}>
        {label}
      </span>
      <span style={{ fontFamily: mono, fontSize: "0.65rem", color: valueColor || themeColors.text }}>
        {value}
      </span>
    </div>
  );
}

/* ─── Panel glow overlay ─────────────────────────────────────────────────── */
function PanelGlow({ color, delay }: { color: string; delay: string }) {
  return (
    <div
      style={{
        position: "absolute",
        inset: -1,
        borderRadius: "12px",
        border: `1px solid ${color}`,
        boxShadow: `0 0 20px ${color}40, inset 0 0 20px ${color}08`,
        pointerEvents: "none",
        opacity: 0,
        animation: `glowPulse ${CYCLE} ease-in-out ${delay} infinite`,
      }}
    />
  );
}

/* ═══════════════════════════════════════════════════════════════════════════ */
/*  MAIN COMPONENT                                                           */
/* ═══════════════════════════════════════════════════════════════════════════ */
export default function HeroProductDemo() {
  const [isMobile, setIsMobile] = useState(false);
  const { theme } = useTheme();
  const isDark = theme === "dark";

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  /* theme-aware colors */
  const themeColors = getThemeColors(isDark);

  /* panel base style */
  const panelBase: React.CSSProperties = {
    position: "relative",
    background: themeColors.bg,
    border: `1px solid ${themeColors.border}`,
    borderRadius: "12px",
    padding: "22px 20px",
    display: "flex",
    flexDirection: "column",
    overflow: "visible",
  };

  /* stagger entrance animations */
  const stagger = (index: number): React.CSSProperties => ({
    opacity: 0,
    animation: `fadeInUp 0.55s ease-out ${index * 150}ms forwards`,
  });

  return (
    <>
      <style>{keyframes}</style>
      <div
        style={{
          width: "100%",
          maxWidth: "1200px",
          margin: "0 auto",
          padding: isMobile ? "0 16px" : "0 24px",
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            alignItems: isMobile ? "center" : "stretch",
            gap: "0",
          }}
        >
          {/* ── LEFT PANEL: LinkedHash Data ───────────────────────────────── */}
          <div
            style={{
              ...panelBase,
              flex: isMobile ? "none" : "1 1 25%",
              minWidth: 0,
              width: isMobile ? "100%" : undefined,
              ...stagger(0),
            }}
          >
            <PanelGlow color={themeColors.cyan} delay="0s" />

            <div
              style={{
                fontFamily: mono,
                fontSize: "0.58rem",
                textTransform: "uppercase",
                letterSpacing: "0.12em",
                color: themeColors.textMuted,
                marginBottom: "14px",
              }}
            >
              LinkedHash Data
            </div>

            <div style={{ display: "flex", alignItems: "baseline", gap: "8px", marginBottom: "4px" }}>
              <span style={{ fontFamily: mono, fontSize: "0.8rem", fontWeight: 700, color: themeColors.text }}>
                AAPL
              </span>
              <span style={{ fontFamily: mono, fontSize: "0.6rem", color: themeColors.textDim }}>
                {"\u00B7"} Stock
              </span>
            </div>

            <div style={{ display: "flex", alignItems: "baseline", gap: "8px", marginBottom: "16px" }}>
              <span style={{ fontFamily: mono, fontSize: "1.1rem", fontWeight: 700, color: themeColors.text }}>
                $198.52
              </span>
              <span style={{ fontFamily: mono, fontSize: "0.7rem", color: themeColors.green, fontWeight: 600 }}>
                +2.34%
              </span>
            </div>

            <div
              style={{
                borderTop: `1px solid ${themeColors.border}`,
                paddingTop: "10px",
                display: "flex",
                flexDirection: "column",
                gap: "4px",
              }}
            >
              <DataRow label="IV Rank" value="82nd pctl" valueColor={themeColors.cyan} />
              <DataRow label="Put/Call" value="0.87" />
              <DataRow label="Skew" value="-4.2" />
              <DataRow label="Next Event" value="FOMC Mar 19" valueColor={themeColors.cyan} />
            </div>

            <div style={{ display: "flex", flexWrap: "wrap", gap: "5px", marginTop: "16px" }}>
              <Pill>Options</Pill>
              <Pill>Fundamentals</Pill>
              <Pill>Macro</Pill>
            </div>
          </div>

          {/* ── ARROW: Left → Center ──────────────────────────────────────── */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: isMobile ? "8px 0" : "0 10px",
              flexShrink: 0,
              ...stagger(0),
            }}
          >
            <Arrow vertical={isMobile} delay="1.5s" />
          </div>

          {/* ── CENTER PANEL: Analysis in Claude ─────────────────────────── */}
          <div
            style={{
              ...panelBase,
              background: themeColors.bgCenter,
              flex: isMobile ? "none" : "1.6 1 40%",
              minWidth: 0,
              width: isMobile ? "100%" : undefined,
              ...stagger(1),
            }}
          >
            <PanelGlow color={themeColors.claude} delay="2s" />

            {/* Panel header with Claude logo */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: "16px",
              }}
            >
              <span
                style={{
                  fontFamily: sans,
                  fontSize: "0.85rem",
                  fontWeight: 600,
                  color: themeColors.text,
                }}
              >
                Claude
              </span>
              <div
                style={{
                  width: "30px",
                  height: "30px",
                  borderRadius: "7px",
                  background: themeColors.claude,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <ClaudeLogo size={18} />
              </div>
            </div>

            {/* Tool use indicator */}
            <div
              style={{
                borderLeft: `2px solid ${themeColors.cyan}`,
                marginBottom: "16px",
                background: "rgba(0,184,219,0.04)",
                borderRadius: "0 6px 6px 0",
                padding: "8px 12px",
              }}
            >
              <span
                style={{
                  fontFamily: mono,
                  fontSize: "0.6rem",
                  textTransform: "uppercase",
                  letterSpacing: "0.1em",
                  color: themeColors.cyan,
                }}
              >
                Using LinkedHash Market Intelligence
              </span>
            </div>

            {/* Claude response text */}
            <div
              style={{
                fontFamily: sans,
                fontSize: "0.78rem",
                color: isDark ? "rgba(255,255,255,0.82)" : "rgba(17,17,16,0.85)",
                lineHeight: 1.7,
                display: "flex",
                flexDirection: "column",
                gap: "12px",
              }}
            >
              <p style={{ margin: 0 }}>
                IV rank is at the{" "}
                <span style={{ color: themeColors.cyan, fontWeight: 600 }}>82nd percentile</span>{" "}
                with elevated put skew (
                <span style={{ fontFamily: mono, fontSize: "0.75rem" }}>-4.2</span>)
                suggesting institutional hedging ahead of FOMC on March 19th.
              </p>
              <p style={{ margin: 0 }}>
                The risk/reward profile favors waiting for post-event IV crush before
                entering a long position. Current support at{" "}
                <span style={{ fontFamily: mono, fontSize: "0.75rem" }}>$194</span>{" "}
                provides a clean stop-loss level.
              </p>
              <p style={{ margin: 0, fontWeight: 600 }}>
                Recommendation:{" "}
                <span style={{ fontWeight: 400 }}>
                  Wait for post-FOMC clarity. Monitor IV rank for drop below 60th
                  percentile as entry signal
                </span>
                <span
                  style={{
                    display: "inline-block",
                    width: "2px",
                    height: "1em",
                    background: themeColors.text,
                    marginLeft: "2px",
                    verticalAlign: "text-bottom",
                    animation: "blink 1s step-end infinite",
                  }}
                />
              </p>
            </div>
          </div>

          {/* ── ARROW: Center → Right ─────────────────────────────────────── */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: isMobile ? "8px 0" : "0 10px",
              flexShrink: 0,
              ...stagger(2),
            }}
          >
            <Arrow vertical={isMobile} delay="3.5s" />
          </div>

          {/* ── RIGHT PANEL: LinkedHash Monitoring ────────────────────────── */}
          <div
            style={{
              ...panelBase,
              flex: isMobile ? "none" : "1 1 25%",
              minWidth: 0,
              width: isMobile ? "100%" : undefined,
              ...stagger(2),
            }}
          >
            <PanelGlow color={themeColors.green} delay="4s" />

            <div
              style={{
                fontFamily: mono,
                fontSize: "0.58rem",
                textTransform: "uppercase",
                letterSpacing: "0.12em",
                color: themeColors.textMuted,
                marginBottom: "14px",
              }}
            >
              LinkedHash Monitoring
            </div>

            <div
              style={{
                fontFamily: sans,
                fontSize: "0.8rem",
                fontWeight: 600,
                color: themeColors.text,
                marginBottom: "12px",
                display: "flex",
                alignItems: "center",
                gap: "6px",
              }}
            >
              <span>Active Watch</span>
              <span style={{ color: themeColors.textDim }}>{"\u00B7"}</span>
              <span style={{ color: themeColors.cyan }}>AAPL</span>
            </div>

            <div style={{ marginBottom: "12px" }}>
              <div
                style={{
                  fontFamily: mono,
                  fontSize: "0.58rem",
                  textTransform: "uppercase",
                  letterSpacing: "0.1em",
                  color: themeColors.textDim,
                  marginBottom: "4px",
                }}
              >
                Condition
              </div>
              <div
                style={{
                  fontFamily: sans,
                  fontSize: "0.72rem",
                  color: isDark ? "rgba(255,255,255,0.75)" : "rgba(17,17,16,0.75)",
                  lineHeight: 1.5,
                }}
              >
                IV rank drops below 60th pctl
              </div>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: "4px", marginBottom: "16px" }}>
              <DataRow label="Status" value="Monitoring" valueColor={themeColors.cyan} />
              <DataRow label="Check interval" value="5 min" />
              <DataRow label="Channel" value="Slack" />
            </div>

            <div style={{ height: "1px", background: themeColors.border, margin: "4px 0 14px" }} />

            {/* Alert section */}
            <div
              style={{
                borderLeft: `2px solid ${themeColors.green}`,
                background: themeColors.greenBg,
                borderRadius: "0 6px 6px 0",
                padding: "10px 12px",
              }}
            >
              <div
                style={{
                  fontFamily: sans,
                  fontSize: "0.75rem",
                  fontWeight: 700,
                  color: themeColors.green,
                  marginBottom: "6px",
                  display: "flex",
                  alignItems: "center",
                  gap: "5px",
                }}
              >
                <span>{"\u26A1"}</span>
                <span>Alert Triggered</span>
              </div>
              <div
                style={{
                  fontFamily: sans,
                  fontSize: "0.7rem",
                  color: isDark ? "rgba(255,255,255,0.75)" : "rgba(17,17,16,0.75)",
                  lineHeight: 1.6,
                  marginBottom: "6px",
                }}
              >
                AAPL IV rank hit{" "}
                <span style={{ color: themeColors.green, fontWeight: 600 }}>58th pctl</span>.<br />
                Condition met — entry signal.
              </div>
              <TimestampFade />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

/* ─── Subtle timestamp fade animation ────────────────────────────────────── */
function TimestampFade() {
  const [alt, setAlt] = useState(false);
  const { theme } = useTheme();
  const isDark = theme === "dark";
  const themeColors = getThemeColors(isDark);

  useEffect(() => {
    const interval = setInterval(() => setAlt((v) => !v), 4000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div
      style={{
        fontFamily: mono,
        fontSize: "0.58rem",
        color: themeColors.textDim,
        transition: "opacity 0.6s ease",
      }}
    >
      {alt ? "just now" : "2 min ago"}{" "}
      <span style={{ color: isDark ? "rgba(255,255,255,0.2)" : "rgba(17,17,16,0.2)" }}>{"\u00B7"}</span> via Slack
    </div>
  );
}
